import './styles/ManageUsers.scss';

import { useMutation, useQuery } from '@apollo/client';
import { CloudDownload, Create } from '@mui/icons-material';
import { Button, Grid, Icon, Paper, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { IOUser as IOUserType } from '@thro-types/users';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DELETE_MUTATION from 'gql/mutations/InternationalOffice/deleteIOUser.gql';
import QUERY from 'gql/queries/getUsers.gql';

import { TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

import { IOUser } from '../IOUser';

export function ManageUsers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<{} | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const [deleteIOUser] = useMutation(DELETE_MUTATION, { context: { clientName: 'federation' } });

  const keywordState = useState();

  // const [pageSize, setPageSize] = useState(25);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  usePageHeading({
    heading: t(translations.pages.internationalOffice.manageUsers()),
    mobilePadding: false,
    className: 'io-users-management'
  });

  const usersQuery = useQuery<
    any,
    {
      type?: string;
      application?: string;
      offset?: number;
      limit?: number;
    }
  >(QUERY, {
    variables: { type: UserType.INCOMING, offset: 0, limit: paginationModel.pageSize },
    context: { clientName: 'federation' }
  });
  const { refetch } = usersQuery;

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleDelete = async userId => {
    try {
      await deleteIOUser({
        variables: {
          id: userId
        },
        refetchQueries: ['GetUsers']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.internationalOffice.users.alerts.success.deletedUser())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(
          t(translations.pages.internationalOffice.users.alerts.error.deletingUser())
        )
      );
    }
  };

  const filters: any[] | undefined = [
    {
      field: 'keyword',
      label: t(translations.pages.internationalOffice.users.search()),
      stateHandler: keywordState
    }
  ];

  const ControlButtons = () => {
    return (
      <>
        <Button
          startIcon={<CloudDownload />}
          variant="outlined"
          color="primary"
          className="users-management__actions--secondary"
          onClick={() => {
            window.open(`${import.meta.env.VITE_GATEWAY_URL}/reports/students`, '_blank');
          }}
        >
          {t(translations.common.export())}
        </Button>
        <Button
          startIcon={<Create />}
          variant="contained"
          className="users-management__actions--primary"
          onClick={() => {
            setCreateModalOpen(true);
          }}
        >
          {t(translations.common.create())}
        </Button>
      </>
    );
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => setEditingUser(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => setDeletingUser(params.id)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-file-pdf"
              style={{
                width: 'auto'
              }}
            />
          }
          label={t(translations.common.export())}
          onClick={() =>
            window.open(
              `${import.meta.env.VITE_GATEWAY_URL}/reports/students/${params.id}`,
              '_blank'
            )
          }
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<IOUserType> => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.common.email()),
        field: 'email',
        renderCell: params => (
          <Tooltip title={params.row.email}>
            <span>{params.row.email}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.common.firstName()),
        field: 'firstName',
        renderCell: params => (
          <Tooltip title={params.row.firstName}>
            <span>{params.row.firstName}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.common.lastName()),
        field: 'lastName',
        renderCell: params => (
          <Tooltip title={params.row.lastName}>
            <span>{params.row.lastName}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.internationalOffice.users.homeUniversity()),
        field: 'homeUniversity',
        renderCell: params => (
          <Tooltip title={params.row.homeUniversity}>
            <span>{params.row.homeUniversity}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 160,
        headerName: t(translations.pages.internationalOffice.users.courseOfStudies()),
        field: 'courseOfStudies',
        renderCell: params => (
          <Tooltip title={params.row.courseOfStudies}>
            <span>{params.row.courseOfStudies}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.pages.internationalOffice.users.semester()),
        field: 'semester'
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <>
      <ErrorBoundary boundaryTag="ioUserMangegementBoundary">
        <Modal
          open={createModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCreateModalOpen(false);
          }}
          headline={t(translations.pages.internationalOffice.users.newIOUser())}
          onCancel={() => {
            setCreateModalOpen(false);
          }}
          disabledCancel={disabledCancel}
        >
          <IOUser
            onCancel={() => setCreateModalOpen(false)}
            closeHandler={() => {
              setCreateModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="ioUserModalBoundary">
        <Modal
          open={editingUser !== null}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setEditingUser(null);
          }}
          headline={t(translations.pages.internationalOffice.users.editIOUser())}
          onCancel={() => {
            setEditingUser(null);
          }}
          disabledCancel={disabledCancel}
        >
          <IOUser
            onCancel={() => setEditingUser(null)}
            closeHandler={() => setEditingUser(null)}
            setDisabledCancel={setDisabledCancel}
            user={editingUser}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deletingUser !== null}
        title={t(translations.pages.internationalOffice.users.deleteConfirm.title())}
        onClose={() => setDeletingUser(null)}
        onConfirm={() => {
          handleDelete(deletingUser);
          setDeletingUser(null);
        }}
      >
        <div>{t(translations.pages.internationalOffice.users.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <GQLGuard
        query={usersQuery}
        onError={error => {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.internationalOffice.users.alerts.error.loadingUsers())}`
            )
          );
          navigate('/', { replace: true });
          return null;
        }}
      >
        {usersData => {
          let usersCount = usersData.usersCount;
          return (
            <>
              <Grid
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  paddingBottom: '1em'
                }}
                container
              >
                <ControlButtons />
              </Grid>
              <Grid container flexGrow={1} className="io-users-management__paper" component={Paper}>
                <DataGrid
                  slotProps={{
                    toolbar: {
                      filters: {
                        filterConfig: filters,
                        onFilterChange: refetch
                      }
                    }
                  }}
                  disableColumnFilter
                  disableRowSelectionOnClick
                  columns={columns.map(column => ({
                    ...column,
                    sortable: false
                  }))}
                  rows={usersData.users}
                  paginationMode="server"
                  // onPageSizeChange={handlePageSizeChange}
                  // pageSize={pageSize}
                  rowCount={usersCount}
                  paginationModel={{
                    page: paginationModel.page,
                    pageSize: paginationModel.pageSize
                  }}
                  onPaginationModelChange={pagModel => {
                    handlePaginationChange(pagModel);
                    handlePagination(null, pagModel.page);
                  }}
                  // onPageChange={(page, details) => handlePagination(details, page)}
                />
              </Grid>
            </>
          );
        }}
      </GQLGuard>
    </>
  );
}
